import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

const ImageCropper = forwardRef(({ onCropped, aspectRatio = 1, maxWidth = 600, maxHeight = 600, showUploadButton = true }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const imageRef = useRef(null);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);

  const destroyCropper = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => destroyCropper();
  }, [destroyCropper]);

  useEffect(() => {
    if (showModal && imageSrc && imageRef.current && window.Cropper) {
      destroyCropper();
      setTimeout(() => {
        cropperRef.current = new window.Cropper(imageRef.current, {
          aspectRatio,
          viewMode: 1,
          autoCropArea: 0.8,
          movable: true,
          zoomable: true,
          cropBoxMovable: true,
          cropBoxResizable: true,
        });
      }, 100);
    }
  }, [showModal, imageSrc, aspectRatio, destroyCropper]);

  const openWithUrl = useCallback((url) => {
    setImageSrc(url);
    setShowModal(true);
  }, []);

  useImperativeHandle(ref, () => ({
    openWithUrl,
  }), [openWithUrl]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageSrc(ev.target.result);
      setShowModal(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCrop = () => {
    if (!cropperRef.current) return;
    const canvas = cropperRef.current.getCroppedCanvas({
      width: maxWidth,
      height: maxHeight,
    });
    canvas.toBlob((blob) => {
      const formData = new FormData();
      formData.append('files[]', blob, `cropped_${Date.now()}.jpg`);
      fetch('/api/admin-files.php?action=upload', {
        method: 'POST',
        body: formData,
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            fetch('/api/admin-files.php?action=list')
              .then((r) => r.json())
              .then((listData) => {
                const files = listData.files || [];
                const latest = files[0];
                if (latest) {
                  onCropped({ url: latest.url });
                }
              });
          }
        });
      closeModal();
    }, 'image/jpeg', 0.9);
  };

  const closeModal = () => {
    destroyCropper();
    setShowModal(false);
    setImageSrc(null);
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        className="hidden"
      />
      {showUploadButton && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700"
        >
          写真をアップロード
        </button>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="text-sm font-bold">写真をトリミング</span>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="p-4">
              <div style={{ maxHeight: '60vh', overflow: 'hidden' }}>
                {imageSrc && (
                  <img
                    ref={imageRef}
                    src={imageSrc}
                    alt="トリミング"
                    style={{ maxWidth: '100%', display: 'block' }}
                  />
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-4 py-3 border-t">
              <button onClick={closeModal} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
                キャンセル
              </button>
              <button onClick={handleCrop} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">
                この位置で決定
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default ImageCropper;
